/**
 * Shared language selection + colour palette for the Programming Language
 * Activity chart. Used by BOTH renderers so they show the same languages, in
 * the same stack/legend order, in the same colours:
 *   - tools/skills-chart-generator.mjs   (static profile README SVGs)
 *   - src/components/LocalSkillsChart.vue (site chart)
 *
 * Plain ES module, no dependencies (Node imports it directly; webpack/babel
 * bundles it for the site).
 *
 * ── Selection policy (selectLanguages) ──────────────────────────────────────
 * Input is the raw monthly series (skills-merged.json `scores`, decay-presence,
 * 365-day half-life). Months before `fromMonth` (sparse start of the record)
 * are ignored for every statistic below.
 *   1. peak share  = a language's maximum share of the stacked monthly total.
 *   2. qualifying  = peak share >= `peakThreshold` (T).
 *   3. qualifying languages are taken in descending peak-share order until
 *      "Other" (everything not shown) is <= `otherTarget` in EVERY month, or
 *      `cap` languages are shown. A candidate that holds < `minHelpShare` of
 *      the total in every month still over target is skipped (it cannot move
 *      the remaining violations and would only spend a slot).
 *   4. If the cap is hit first, `worstOther` reports the month that is still
 *      furthest over the target.
 *   Groups (SELECTION_DEFAULTS.groups = DEFAULT_GROUPS; pass [] for none):
 *   each group ({name, color, members, mergeInto?}) is a named layer that
 *   replaces its member languages (scores summed, see groupScores). A group
 *   present in the data is pinned first and occupies one slot of `cap`.
 *   `pinned` languages (default ['Dart']) present in the data are pinned next.
 *   The remaining slots are filled by steps 1-3. Renderers drawing a grouped
 *   plan must draw from plan.scores (or groupScores(scores, groups)), not the
 *   raw scores. DEFAULT_GROUPS and why:
 *     - "Build & Infrastructure" (BUILD_INFRA_GROUP): build systems, container
 *       and infra/policy config. Individually small, together a large share of
 *       the recent work; one slate layer instead of 4-5 thin ones.
 *     - "HTML & templates" (HTML_TEMPLATES_GROUP, mergeInto HTML): template
 *       languages are HTML with holes, so they fold into HTML's layer and keep
 *       HTML's colour.
 *     - "Specs & schemas" (SPECS_SCHEMAS_GROUP): IDL / schema / executable-spec
 *       DSLs (Protocol Buffer, Gherkin, ...).
 *   Grouping frees slots, so smaller real languages fit; Dart is pinned
 *   (operator choice: current work, peaks only in the latest month at ~3%).
 *
 * ── Stack / legend order (orderLanguages) ───────────────────────────────────
 * By first appearance (first month with a non-zero score; ties by all-time
 * total, descending), bottom of the stack first: the stack reads as career
 * strata, newer languages pile on top, and the legend reads chronologically.
 * The legend uses the stack order, with "Other" last (it is the top layer).
 * The order is then minimally perturbed (moves of <= MAX_SHIFT places) when two
 * layers that actually touch somewhere in the chart are too close in colour.
 *
 * ── Colours ─────────────────────────────────────────────────────────────────
 * LINGUIST_COLORS are github-linguist's languages.yml `color` values, exact,
 * generated by tools/linguist-colors-sync.mjs (pinned commit noted at the table).
 * Lookup is case-insensitive so data keys like "Vim Script" (ledger/GitHub
 * spelling) resolve to linguist's "Vim script".
 * COLOR_OVERRIDES is the fixed table of minimal nudges applied on top; every
 * entry documents why. "Other" is neutral grey.
 *
 * ── Distinguishability ──────────────────────────────────────────────────────
 * Colour difference = Euclidean distance in OKLab (ΔE_OK, L in 0..1), measured
 * on the colour as actually drawn: the area fill (opacity AREA_OPACITY)
 * composited over each theme background in THEME_BACKGROUNDS, worst case
 * across themes. Thresholds (DELTA_E):
 *   adjacent 0.08 — two layers that share a border anywhere in the chart.
 *                   ~0.08 OKLab ≈ CIEDE2000 7–8: clearly different at a glance.
 *   legend   0.05 — any two shown legend entries (chips are labelled, so
 *                   "distinct, not merely nearby" is enough).
 *   background 0.06 — any layer against the page background (the top layer
 *                   and thin layers border the background).
 * Adjacency is "effective": in every month, layers thinner than
 * VISIBLE_SHARE of the total are skipped, so two layers separated only by a
 * vanished layer still count as touching.
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

// SELECTION_DEFAULTS (exported below DEFAULT_GROUPS) = these + groups: DEFAULT_GROUPS.
const SELECTION_BASE = {
  peakThreshold: 0.02, // T: minimum peak monthly share to qualify
  otherTarget: 0.1, // "Other" must stay <= this share in every month (from fromMonth)
  cap: 16, // hard cap on shown languages (excl. "Other")
  fromMonth: '2013-01', // ignore the sparse first months of the record
  minHelpShare: 0.005, // see policy step 3
  pinned: Object.freeze(['Dart']), // always shown when present in the data; see policy note
};

export const DELTA_E = Object.freeze({ adjacent: 0.08, legend: 0.05, background: 0.06 });

export const AREA_OPACITY = 0.9;
export const VISIBLE_SHARE = 0.005;
export const MAX_SHIFT = 3;

/** Backgrounds each theme is checked against: site card / page, GitHub README. */
export const THEME_BACKGROUNDS = Object.freeze({
  light: ['#ffffff'],
  dark: ['#1e1e1e', '#121212', '#0d1117'],
});

export const OTHER = 'Other';
export const OTHER_COLOR = '#BDC3C7';

/** github-linguist languages.yml colours, exact upstream values (generated). */
// Generated by tools/linguist-colors-sync.mjs from github-linguist/linguist@681a3171a3f8d1ccab93e646e7753cba42186915 (2026-10-14T08:47:15Z).
// Do not hand-edit: nudges go in COLOR_OVERRIDES; re-run the tool to refresh.
export const LINGUIST_COLORS = Object.freeze({
  ActionScript: '#882B0F',
  Astro: '#ff5a03',
  C: '#555555',
  'C#': '#7355dd',
  'C++': '#f34b7d',
  CMake: '#DA3434',
  CoffeeScript: '#244776',
  CSS: '#663399',
  Dart: '#00B4AB',
  Dockerfile: '#384d54',
  Elixir: '#8847B9',
  Gherkin: '#5B2063',
  Go: '#00ADD8',
  GraphQL: '#e10098',
  Groovy: '#4298b8',
  HCL: '#844FBA',
  HTML: '#e34c26',
  'HTML+EEX': '#6e4a7e',
  Java: '#b07219',
  JavaScript: '#f1e05a',
  Jinja: '#a52a22',
  'Jupyter Notebook': '#DA5B0B',
  Just: '#384d54',
  Kotlin: '#A97BFF',
  Lua: '#000080',
  Makefile: '#427819',
  Markdown: '#083fa1',
  MDX: '#fcb32c',
  'Open Policy Agent': '#7d9199',
  PHP: '#4F5D95',
  PowerShell: '#012456',
  Python: '#3572A5',
  Ruby: '#701516',
  Rust: '#dea584',
  Shell: '#89e051',
  Smarty: '#f0c040',
  Solidity: '#AA6746',
  SQL: '#e38c00',
  Starlark: '#76d275',
  Swift: '#F05138',
  TeX: '#3D6117',
  TypeScript: '#3178c6',
  'Vim script': '#199f4b',
  Vue: '#41b883',
  WebAssembly: '#04133b',
});

/**
 * Fixed override table: minimal nudges away from the linguist colour, each
 * still recognisably the language's own colour. Found with
 * `node tools/language-palette-report.mjs` (prints every failing pair); a
 * nudge is only added when reordering cannot fix the clash (legend pairs are
 * not about adjacency, so reordering never helps them). Keep `why` current.
 */
export const COLOR_OVERRIDES = Object.freeze({
  Python: {
    hex: '#376C99', // linguist #3572A5; ΔE_OK 0.023 from it: L -0.02, chroma -0.01, same hue
    why: 'legend clash with TypeScript #3178c6 (ΔE_OK 0.045 < 0.05); now 0.063. Python takes the nudge so TypeScript keeps its exact brand blue.',
  },
  C: {
    hex: '#5B5B5B', // linguist #555555; ΔE_OK 0.022 from it: L +0.02, still neutral grey
    why: 'legend clash with Dockerfile #384d54 (ΔE_OK 0.046 < 0.05); now 0.063. Pure lightness nudge keeps C grey.',
  },
});

/** Languages linguist gives no colour to (data / config types). */
export const EXTRA_COLORS = Object.freeze({
  'Protocol Buffer': '#B5838D', // dusty rose: >= 0.13 ΔE_OK from every other shown colour
});

/**
 * Default groups. Names are linguist names (matched case-insensitively);
 * members absent from the data are harmless. Each group's colour is checked by
 * the tests against every shown colour, Other and both themes.
 *
 * "Build & Infrastructure": build systems, container / infra definitions and
 * policy config — tooling around the code rather than programming languages.
 * Deliberately NOT members: Shell/PowerShell/Batchfile (scripting), SQL and
 * GraphQL (query languages), YAML/JSON/TOML (not counted by the pipeline).
 * Colour: slate, desaturated but clearly chromatic (unlike Other's grey).
 */
export const BUILD_INFRA_GROUP = Object.freeze({
  name: 'Build & Infrastructure',
  color: '#738595', // slate
  members: Object.freeze([
    // build systems
    'Dockerfile', 'Makefile', 'CMake', 'Starlark', 'Just', 'Meson', 'Ninja', 'Earthly', 'Nix', 'Bicep',
    // infra / config / policy languages
    'HCL', 'Jsonnet', 'CUE', 'Dhall', 'Nginx', 'Puppet', 'Open Policy Agent',
  ]),
});

/**
 * Template languages merged INTO HTML: the layer keeps HTML's colour and is
 * labelled "HTML & templates". `mergeInto` names the host language.
 */
export const HTML_TEMPLATES_GROUP = Object.freeze({
  name: 'HTML & templates',
  mergeInto: 'HTML',
  color: LINGUIST_COLORS.HTML,
  members: Object.freeze([
    'HTML',
    'Jinja', 'Smarty', 'HTML+EEX', 'HTML+ERB', 'HTML+Razor', 'Go Template', 'Handlebars', 'Mustache', 'Liquid', 'Twig', 'Mako', 'Nunjucks', 'Pug', 'Haml', 'Slim',
  ]),
});

/**
 * "Specs & schemas": interface-definition / schema languages and executable
 * spec DSLs. Only clearly spec/schema DSLs: GraphQL is excluded (linguist
 * counts queries and schemas together, mostly queries), as is SQL.
 * Colour: dusty rose (was Protocol Buffer's EXTRA_COLORS entry).
 */
export const SPECS_SCHEMAS_GROUP = Object.freeze({
  name: 'Specs & schemas',
  color: '#B5838D',
  members: Object.freeze(['Protocol Buffer', 'Thrift', "Cap'n Proto", 'Avro IDL', 'Gherkin', 'RAML', 'API Blueprint']),
});

export const DEFAULT_GROUPS = Object.freeze([BUILD_INFRA_GROUP, HTML_TEMPLATES_GROUP, SPECS_SCHEMAS_GROUP]);

export const SELECTION_DEFAULTS = Object.freeze({ ...SELECTION_BASE, groups: DEFAULT_GROUPS });

/**
 * Replace group members by their group name in raw scores (scores summed per
 * entry). Idempotent; returns `scores` unchanged when there are no groups.
 */
export function groupScores(scores, groups = []) {
  if (!groups || !groups.length) return scores;
  const to = new Map();
  for (const g of groups) for (const m of g.members) to.set(String(m).toLowerCase(), g.name);
  return (scores || []).map(s => {
    const sums = new Map();
    for (const l of s.languages || []) {
      const k = to.get(String(l.language).toLowerCase()) || l.language;
      sums.set(k, (sums.get(k) || 0) + (Number(l.score) || 0));
    }
    return { ...s, languages: [...sums].map(([language, score]) => ({ language, score })) };
  });
}

const LINGUIST_BY_LOWER = new Map(Object.entries(LINGUIST_COLORS).map(([k, v]) => [k.toLowerCase(), v]));

/** Deterministic fallback for languages with no colour at all. */
const FALLBACK_COLORS = ['#8E9AAF', '#B5838D', '#6D9DC5', '#A3B18A', '#C9ADA7', '#7F7F9F'];

export function languageColor(lang) {
  if (lang === OTHER) return OTHER_COLOR;
  if (COLOR_OVERRIDES[lang]) return COLOR_OVERRIDES[lang].hex;
  if (LINGUIST_COLORS[lang]) return LINGUIST_COLORS[lang];
  const ci = LINGUIST_BY_LOWER.get(String(lang).toLowerCase());
  if (ci) return ci;
  if (EXTRA_COLORS[lang]) return EXTRA_COLORS[lang];
  let h = 0;
  for (const ch of String(lang)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length];
}

// ---------------------------------------------------------------------------
// OKLab
// ---------------------------------------------------------------------------

export function hexToRgb(hex) {
  let h = String(hex).replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
}

const toLinear = c => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

export function rgbToOklab([r, g, b]) {
  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** Composite `fg` at `alpha` over opaque `bg` (sRGB, as browsers do). */
export function composite(fg, bg, alpha = AREA_OPACITY) {
  const f = hexToRgb(fg);
  const b = hexToRgb(bg);
  return f.map((c, i) => alpha * c + (1 - alpha) * b[i]);
}

export function deltaE(a, b) {
  const p = rgbToOklab(Array.isArray(a) ? a : hexToRgb(a));
  const q = rgbToOklab(Array.isArray(b) ? b : hexToRgb(b));
  return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
}

/** Worst-case ΔE_OK between two area fills across every theme background. */
export function drawnDeltaE(c1, c2, backgrounds = THEME_BACKGROUNDS) {
  let min = Infinity;
  for (const bgs of Object.values(backgrounds)) {
    for (const bg of bgs) min = Math.min(min, deltaE(composite(c1, bg), composite(c2, bg)));
  }
  return min;
}

/** Worst-case ΔE_OK between an area fill and the background, per theme. */
export function backgroundDeltaE(c, backgrounds = THEME_BACKGROUNDS) {
  const out = {};
  for (const [theme, bgs] of Object.entries(backgrounds)) {
    out[theme] = Math.min(...bgs.map(bg => deltaE(composite(c, bg), bg)));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Monthly series
// ---------------------------------------------------------------------------

/**
 * Raw `scores` ([{date, languages:[{language, score}]}]) -> sorted monthly rows
 * [{month:'YYYY-MM', values:Map<lang,score>, total}]. Multiple entries in a
 * month are summed.
 */
export function monthlyRows(scores) {
  const byMonth = new Map();
  for (const s of scores || []) {
    const month = String(s.date).slice(0, 7);
    let row = byMonth.get(month);
    if (!row) byMonth.set(month, (row = { month, values: new Map(), total: 0 }));
    for (const l of s.languages || []) {
      const v = Number(l.score) || 0;
      if (v <= 0) continue;
      row.values.set(l.language, (row.values.get(l.language) || 0) + v);
      row.total += v;
    }
  }
  return [...byMonth.values()].sort((a, b) => (a.month < b.month ? -1 : 1));
}

function otherShares(rows, shown) {
  return rows.map(r => {
    if (!(r.total > 0)) return 0;
    let o = 0;
    for (const [k, v] of r.values) if (!shown.has(k)) o += v;
    return o / r.total;
  });
}

function worst(rows, shares) {
  let i = 0;
  for (let j = 1; j < shares.length; j++) if (shares[j] > shares[i]) i = j;
  return rows.length ? { share: shares[i], month: rows[i].month } : { share: 0, month: null };
}

/** Max "Other" share (from `fromMonth`) when only `shown` languages are drawn. */
export function maxOtherShare(scores, shown, fromMonth = SELECTION_DEFAULTS.fromMonth) {
  const rows = monthlyRows(scores).filter(r => r.month >= fromMonth);
  return worst(rows, otherShares(rows, new Set(shown)));
}

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

export function languageStats(scores, fromMonth = SELECTION_DEFAULTS.fromMonth) {
  const rows = monthlyRows(scores).filter(r => r.month >= fromMonth);
  const stats = new Map();
  for (const r of rows) {
    for (const [k, v] of r.values) {
      let s = stats.get(k);
      if (!s) stats.set(k, (s = { language: k, peakShare: 0, peakMonth: null, total: 0, firstMonth: r.month }));
      const sh = r.total > 0 ? v / r.total : 0;
      if (sh > s.peakShare) {
        s.peakShare = sh;
        s.peakMonth = r.month;
      }
      s.total += v;
    }
  }
  return { rows, stats };
}

/**
 * @returns {{shown:string[], stats:Map, otherLanguages:string[], worstOther:{share,month},
 *            capHit:boolean, skipped:string[], options:object}}
 *   shown is in selection (peak-share) order; use orderLanguages for drawing.
 */
export function selectLanguages(scores, options = {}) {
  const o = { ...SELECTION_DEFAULTS, ...options };
  const { rows, stats } = languageStats(scores, o.fromMonth);
  const ranked = [...stats.values()]
    .filter(s => s.peakShare >= o.peakThreshold)
    .sort((a, b) => b.peakShare - a.peakShare || b.total - a.total);
  const groupNames = new Set((o.groups || []).map(g => g.name));
  const pins = [...groupNames, ...(o.pinned || [])].filter(l => stats.has(l));
  const shown = new Set(pins.slice(0, o.cap));
  const skipped = [];
  let shares = otherShares(rows, shown);
  for (const s of ranked) {
    if (shown.has(s.language)) continue;
    if (shares.every(x => x <= o.otherTarget)) break;
    if (shown.size >= o.cap) break;
    const helps = rows.some((r, i) => shares[i] > o.otherTarget && (r.values.get(s.language) || 0) / r.total >= o.minHelpShare);
    if (!helps) {
      skipped.push(s.language);
      continue;
    }
    shown.add(s.language);
    shares = otherShares(rows, shown);
  }
  const w = worst(rows, shares);
  return {
    shown: [...shown],
    stats,
    otherLanguages: [...stats.keys()].filter(k => !shown.has(k)).sort(),
    worstOther: w,
    capHit: w.share > o.otherTarget && shown.size >= o.cap,
    skipped,
    options: o,
  };
}

// ---------------------------------------------------------------------------
// Ordering + adjacency
// ---------------------------------------------------------------------------

/**
 * Pairs of layers that touch somewhere: per month, consecutive layers after
 * dropping those thinner than VISIBLE_SHARE of the month total. `order` is
 * bottom-to-top and may include OTHER (computed from the non-shown rest).
 * @returns {Map<string, {a:string,b:string,months:number}>} key "a|b"
 */
export function effectiveAdjacency(rows, order, visibleShare = VISIBLE_SHARE) {
  const shownSet = new Set(order.filter(l => l !== OTHER));
  const pairs = new Map();
  for (const r of rows) {
    if (!(r.total > 0)) continue;
    let other = 0;
    for (const [k, v] of r.values) if (!shownSet.has(k)) other += v;
    const visible = order.filter(l => (l === OTHER ? other : r.values.get(l) || 0) / r.total >= visibleShare);
    for (let i = 1; i < visible.length; i++) {
      const key = `${visible[i - 1]}|${visible[i]}`;
      const p = pairs.get(key);
      if (p) p.months++;
      else pairs.set(key, { a: visible[i - 1], b: visible[i], months: 1 });
    }
  }
  return pairs;
}

function minAdjacent(rows, order, colorOf) {
  let min = Infinity;
  let pair = null;
  for (const p of effectiveAdjacency(rows, order).values()) {
    const d = drawnDeltaE(colorOf(p.a), colorOf(p.b));
    if (d < min) {
      min = d;
      pair = p;
    }
  }
  return { min, pair };
}

/**
 * Stack order (bottom -> top, OTHER last if `withOther`): first appearance,
 * then minimal moves (<= MAX_SHIFT places) while they raise the minimum
 * effective-adjacent ΔE and it is still below DELTA_E.adjacent.
 */
export function orderLanguages(scores, shown, { fromMonth = SELECTION_DEFAULTS.fromMonth, withOther = true, colorOf = languageColor } = {}) {
  const { rows, stats } = languageStats(scores, fromMonth);
  const base = [...shown].sort((a, b) => {
    const A = stats.get(a);
    const B = stats.get(b);
    return A.firstMonth < B.firstMonth ? -1 : A.firstMonth > B.firstMonth ? 1 : B.total - A.total;
  });
  let order = withOther ? [...base, OTHER] : base;
  const moves = [];
  let cur = minAdjacent(rows, order, colorOf);
  for (let iter = 0; iter < 20 && cur.min < DELTA_E.adjacent; iter++) {
    let best = null;
    const n = withOther ? order.length - 1 : order.length; // OTHER stays on top
    for (let i = 0; i < n; i++) {
      for (let j = Math.max(0, i - MAX_SHIFT); j <= Math.min(n - 1, i + MAX_SHIFT); j++) {
        if (i === j) continue;
        const cand = [...order];
        const [x] = cand.splice(i, 1);
        cand.splice(j, 0, x);
        const m = minAdjacent(rows, cand, colorOf);
        if (m.min > cur.min + 1e-9 && (!best || m.min > best.m.min)) best = { cand, m, lang: x, from: i, to: j };
      }
    }
    if (!best) break;
    moves.push({ language: best.lang, from: best.from, to: best.to, minAdjacentAfter: best.m.min });
    order = best.cand;
    cur = best.m;
  }
  return { order, moves, minAdjacent: cur.min, minPair: cur.pair, rows };
}

// ---------------------------------------------------------------------------
// One-call plan for renderers
// ---------------------------------------------------------------------------

/**
 * Everything a renderer needs: which languages, in which order, which colours.
 * @returns {{order:string[], shown:string[], otherLanguages:string[], colors:Record<string,string>,
 *            selection:object, ordering:object, groups:object[], scores:object[]}}
 *   order: bottom-to-top, OTHER last when there is anything left over.
 *   scores: the input with `options.groups` applied (=== input when ungrouped).
 */
export function planChart(scores, options = {}) {
  const groups = options.groups || SELECTION_DEFAULTS.groups;
  options = { ...options, groups };
  const grouped = groupScores(scores, groups);
  const groupColor = new Map(groups.map(g => [g.name, g.color]));
  const colorOf = l => groupColor.get(l) || languageColor(l);
  const selection = selectLanguages(grouped, options);
  const withOther = selection.otherLanguages.length > 0;
  const ordering = orderLanguages(grouped, selection.shown, { fromMonth: selection.options.fromMonth, withOther, colorOf });
  const colors = {};
  for (const l of ordering.order) colors[l] = colorOf(l);
  return { order: ordering.order, shown: selection.shown, otherLanguages: selection.otherLanguages, colors, selection, ordering, groups, scores: grouped };
}

/**
 * Distinguishability audit for a plan: min effective-adjacent ΔE, min legend
 * pair ΔE, min background ΔE per theme, and every failing pair.
 */
export function auditPlan(scores, plan) {
  const rows = plan.ordering.rows;
  const colorOf = l => plan.colors[l];
  const adjacent = [...effectiveAdjacency(rows, plan.order).values()].map(p => ({ ...p, dE: drawnDeltaE(colorOf(p.a), colorOf(p.b)) }));
  const legend = [];
  for (let i = 0; i < plan.order.length; i++) {
    for (let j = i + 1; j < plan.order.length; j++) {
      const a = plan.order[i];
      const b = plan.order[j];
      legend.push({ a, b, dE: drawnDeltaE(colorOf(a), colorOf(b)) });
    }
  }
  const background = plan.order.map(l => ({ language: l, ...backgroundDeltaE(colorOf(l)) }));
  const minOf = (arr, f) => arr.reduce((m, x) => (f(x) < f(m) ? x : m), arr[0]);
  return {
    minAdjacent: adjacent.length ? minOf(adjacent, x => x.dE) : null,
    minLegend: legend.length ? minOf(legend, x => x.dE) : null,
    minBackground: background.length ? minOf(background, x => Math.min(x.light, x.dark)) : null,
    failures: [
      ...adjacent.filter(x => x.dE < DELTA_E.adjacent).map(x => ({ kind: 'adjacent', ...x })),
      ...legend.filter(x => x.dE < DELTA_E.legend).map(x => ({ kind: 'legend', ...x })),
      ...background.filter(x => Math.min(x.light, x.dark) < DELTA_E.background).map(x => ({ kind: 'background', ...x })),
    ],
  };
}
